/**
 * Yönlendirme: çok küçük, bağımlılıksız hash tabanlı yönlendirici.
 *
 * Sunucu tarafında SPA fallback rotası **yoktur**; hash yönlendirme her
 * dağıtımda (Debian, statik barındırma, yerel dosya) aynı şekilde çalışır ve
 * sunucuya yalnız `/` isteği düşer.
 */
import { useCallback, useEffect, useState } from 'react';

export type Route =
  | { name: 'home' }
  | { name: 'search'; query: string }
  | { name: 'library'; libraryId: string }
  | { name: 'item'; itemId: string }
  | { name: 'watch'; itemId: string }
  | { name: 'filmler' }
  | { name: 'diziler' }
  | { name: 'muzik' }
  | { name: 'muzik-album'; albumId: string }
  | { name: 'muzik-artist'; artistId: string }
  | { name: 'kutuphaneler' }
  | { name: 'canli-tv' }
  | { name: 'listem' }
  | { name: 'hesap' }
  | { name: 'yonetim' }
  | { name: 'tasarim' };

const SEGMENT_ROUTES = {
  ara: 'search',
  kutuphane: 'library',
  oge: 'item',
  izle: 'watch',
  filmler: 'filmler',
  diziler: 'diziler',
  muzik: 'muzik',
  kutuphaneler: 'kutuphaneler',
  'canli-tv': 'canli-tv',
  listem: 'listem',
  hesap: 'hesap',
  yonetim: 'yonetim',
  tasarim: 'tasarim',
} as const satisfies Record<string, Route['name']>;

/**
 * Hash değerini rota nesnesine çevirir (saf fonksiyon).
 *
 * Tanınmayan veya eksik yollar ana ekrana düşer — kötü bir bağlantı
 * uygulamayı bozmamalı, yalnız içeriği değiştirmelidir.
 */
export function parseHash(hash: string): Route {
  const path = hash.replace(/^#\/?/, '');
  const [head = '', ...rest] = path.split('/');
  const name = SEGMENT_ROUTES[head as keyof typeof SEGMENT_ROUTES];

  if (head === 'muzik' && rest[0] === 'album' && rest[1] !== undefined) {
    return { name: 'muzik-album', albumId: decodeURIComponent(rest[1]) };
  }
  if (head === 'muzik' && rest[0] === 'sanatci' && rest[1] !== undefined) {
    return { name: 'muzik-artist', artistId: decodeURIComponent(rest[1]) };
  }
  if (name === undefined) return { name: 'home' };

  const value = decodeURIComponent(rest.join('/'));

  switch (name) {
    case 'search':
      // Arama hedefi boş sorguyla da açılabilir: mobil "Ara" sekmesi önce
      // arama yüzeyini gösterir, kullanıcı üstteki kutuya yazar.
      return { name: 'search', query: value };
    case 'library':
      return value.length === 0 ? { name: 'home' } : { name: 'library', libraryId: value };
    case 'item':
      return value.length === 0 ? { name: 'home' } : { name: 'item', itemId: value };
    case 'watch':
      return value.length === 0 ? { name: 'home' } : { name: 'watch', itemId: value };
    case 'filmler':
      return { name: 'filmler' };
    case 'diziler':
      return { name: 'diziler' };
    case 'muzik':
      return { name: 'muzik' };
    case 'kutuphaneler':
      return { name: 'kutuphaneler' };
    case 'canli-tv':
      return { name: 'canli-tv' };
    case 'listem':
      return { name: 'listem' };
    case 'hesap':
      // Değer taşımayan yol: `#/hesap` ve `#/hesap/` ikisi de buraya.
      return { name: 'hesap' };
    case 'yonetim':
      return { name: 'yonetim' };
    case 'tasarim':
      return { name: 'tasarim' };
  }
}

/** Rota nesnesini hash değerine çevirir (saf fonksiyon, `parseHash` tersi). */
export function routeToHash(route: Route): string {
  switch (route.name) {
    case 'home':
      return '#/';
    case 'search':
      return route.query.length === 0 ? '#/ara' : `#/ara/${encodeURIComponent(route.query)}`;
    case 'library':
      return `#/kutuphane/${encodeURIComponent(route.libraryId)}`;
    case 'item':
      return `#/oge/${encodeURIComponent(route.itemId)}`;
    case 'watch':
      return `#/izle/${encodeURIComponent(route.itemId)}`;
    case 'filmler':
      return '#/filmler';
    case 'diziler':
      return '#/diziler';
    case 'muzik':
      return '#/muzik';
    case 'muzik-album':
      return `#/muzik/album/${encodeURIComponent(route.albumId)}`;
    case 'muzik-artist':
      return `#/muzik/sanatci/${encodeURIComponent(route.artistId)}`;
    case 'kutuphaneler':
      return '#/kutuphaneler';
    case 'canli-tv':
      return '#/canli-tv';
    case 'listem':
      return '#/listem';
    case 'hesap':
      return '#/hesap';
    case 'yonetim':
      return '#/yonetim';
    case 'tasarim':
      return '#/tasarim';
  }
}

interface RouteHistoryEntry {
  route: Route;
  scrollY: number;
  focusItemId: string | null;
}

/**
 * Uygulama içi geri yığını.
 *
 * Hash yönlendirici tarayıcı geçmişini kullanır; ancak detay/oynatıcıdan
 * dönüşte **hangi listeye** ve **hangi scroll/odak noktasına** dönüleceğini
 * tarayıcı tek başına garanti etmez. Bu yığın yalnız uygulama içi
 * `navigate` çağrılarını izler; doğrudan URL girişinde boştur ve güvenli
 * biçimde ana sayfaya düşer.
 */
const routeStack: RouteHistoryEntry[] = [];
const MAX_ROUTE_STACK = 50;
let pendingRestore: { scrollY: number; focusItemId: string | null } | null = null;

function focusedItemId(): string | null {
  if (typeof document === 'undefined') return null;
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return null;
  // Çok eski TV tarayıcılarında Element.closest bulunmayabilir; odak geri
  // yüklemesi kaybolsun ama uygulama çökmesin.
  if (typeof active.closest !== 'function') return null;
  return active.closest('[data-item-id]')?.getAttribute('data-item-id') ?? null;
}

function restoreContext(): void {
  if (pendingRestore === null) return;
  const { scrollY, focusItemId: itemId } = pendingRestore;
  window.scrollTo(0, scrollY);
  if (itemId !== null) {
    const escaped =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(itemId)
        : itemId.replace(/["\\]/g, '\\$&');
    const target = document.querySelector<HTMLElement>(`[data-item-id="${escaped}"]`);
    target?.focus({ preventScroll: true });
  }
}

export function useRoute(): [Route, (route: Route) => void, (fallback: Route) => void] {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));

  useEffect(() => {
    const onChange = () => {
      setRoute(parseHash(window.location.hash));
      if (pendingRestore !== null) {
        // Liste verisi ilk karede gelebilir; scroll/focus'ı iki kez dene.
        requestAnimationFrame(() => {
          restoreContext();
          window.setTimeout(restoreContext, 120);
        });
        pendingRestore = null;
      }
    };
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  const navigate = useCallback(
    (next: Route) => {
      const hash = routeToHash(next);
      if (window.location.hash === hash) return;
      routeStack.push({ route, scrollY: window.scrollY, focusItemId: focusedItemId() });
      if (routeStack.length > MAX_ROUTE_STACK) routeStack.shift();
      window.location.hash = hash;
    },
    [route],
  );

  const back = useCallback((fallback: Route) => {
    const entry = routeStack.pop();
    if (entry === undefined) {
      // Doğrudan URL ile gelinmiş: geri yığını yok, güvenli hedefe git.
      const hash = routeToHash(fallback);
      if (window.location.hash !== hash) window.location.hash = hash;
      return;
    }
    pendingRestore = { scrollY: entry.scrollY, focusItemId: entry.focusItemId };
    const hash = routeToHash(entry.route);
    if (window.location.hash === hash) {
      restoreContext();
      pendingRestore = null;
      return;
    }
    window.location.hash = hash;
  }, []);

  return [route, navigate, back];
}
